import * as z from 'zod/mini'
import { ApiValidationIssue, validationKeyOf } from '@seiri/shared/common'
import { VALIDATION_SK } from './validation.sk'
import { localeErrorSk } from './zod-locale.sk'

/**
 * Sets the language of every Zod message this app shows - the ones under form fields (Signal Forms
 * `validateStandardSchema`) and the ones `describeApiError` builds from a `validation.failed`.
 * Call it once in `main.ts`, before bootstrap.
 *
 * Zod's built-in issues are worded by `localeErrorSk`, Seiri's own wording rather than Zod's Slovak
 * locale. A Seiri rule carries no message, only `params.key` (see `VALIDATION_MESSAGES_EN`), and
 * `customError` words it from `VALIDATION_SK`; Zod asks `customError` before the locale, and only
 * for an issue without a message of its own.
 */
export const configureZod = () =>
  z.config({
    localeError: localeErrorSk,
    customError: (issue) => {
      const key = issue.code === 'custom' ? validationKeyOf(issue) : undefined
      return key ? VALIDATION_SK[key] : undefined
    },
  })

/** One issue of a `validation.failed` response, worded as the same issue would be in a form. */
export const describeIssue = (issue: ApiValidationIssue): string => {
  const key = validationKeyOf(issue)
  if (key) return VALIDATION_SK[key]
  // The wire issue carries the fields the locale reads (`origin`, `minimum`, …) but not the input,
  // so an `invalid_type` reads as having received nothing - true of the common case, a missing field.
  return localeErrorSk(issue as z.core.$ZodRawIssue)
}
